import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import App from "./App";
import { AuthProvider } from "./contexts/AuthProvider";
import { DesktopTitlebar } from "./components/DesktopTitlebar";
import { LocaleProvider } from "./i18n/LocaleProvider";
import { agentClient } from "./lib/agentClient";
import { captureConfirmLink } from "./lib/authConfirm";

// Before anything renders: a reset link's token leaves the address bar here.
captureConfirmLink();

const root = document.getElementById("root");
if (!root) throw new Error("#root missing from index.html");

/**
 * Smoke-test hook, dev builds only — `import.meta.env.DEV` is a build-time
 * constant, so this whole block is absent from anything shipped.
 *
 * `electron --smoke-agent-restart` drives **this** client rather than
 * reimplementing per-request resolution in the test. A test that reimplements
 * the thing it is testing passes when the real code is broken, which is the
 * failure mode the offline-page false green already cost us once.
 */
if (import.meta.env.DEV) {
  (window as unknown as { __bimgoAgentClient?: unknown }).__bimgoAgentClient = agentClient;

  /**
   * Dev-only stand-in for the desktop bridge, so the shell can be worked on in
   * an ordinary browser **against the real local agent**: `?agent=<port>` points
   * it at the agent the desktop already started, and the rest of the bridge is
   * inert (no instances, no plugin provisioning, links open in a tab). The
   * agent answers only its configured origin, which in dev is this dev server,
   * so a page here is exactly the window it trusts. Absent from shipped builds
   * with the rest of this block (`import.meta.env.DEV` is a build-time constant).
   */
  const agentPort = Number(new URLSearchParams(window.location.search).get("agent"));
  if (!window.bimgo && Number.isInteger(agentPort) && agentPort > 0) {
    const state = { status: "ready" as const, baseUrl: `http://127.0.0.1:${agentPort}`, port: agentPort, pid: null, error: null };
    window.bimgo = {
      app: { info: async () => ({ version: "dev-browser", bridgeVersion: 99, environment: "dev", platform: "browser", arch: "browser" }) },
      agent: { state: async () => state, onStateChange: () => () => {} },
      shell: {
        openExternal: async (url: string) => {
          window.open(url, "_blank", "noopener");
          return true;
        },
      },
      hosts: { list: async () => [] },
      menu: { reload: () => window.location.reload(), toggleDevTools: () => {} },
    };
  }
}

// The custom titlebar exists only inside the desktop window — a plain browser
// has no `window.bimgo` and keeps its own chrome. `window.bimgo` is set by the
// preload before this script runs, so reading it once here is safe.
const hasBridge = typeof window !== "undefined" && Boolean(window.bimgo);

createRoot(root).render(
  <StrictMode>
    <LocaleProvider>
      <AuthProvider>
        {hasBridge ? (
          <div className="desktop-root">
            <DesktopTitlebar />
            <div className="desktop-content">
              <App />
            </div>
          </div>
        ) : (
          <App />
        )}
      </AuthProvider>
    </LocaleProvider>
  </StrictMode>,
);
